import { Component, HostListener, OnInit } from '@angular/core';

interface BlogPost {
  id: number;
  title: string;
  slug: string;
  category: string;
  categoryLabel: string;
  excerpt: string;
  date: string;
  readTime: string;
  image: string;
  featured?: boolean;
}

@Component({
  selector: 'app-news-stories',
  templateUrl: './news-stories.component.html',
  styleUrls: ['./news-stories.component.css']
})
export class NewsStoriesComponent implements OnInit {
  searchText: string = '';
  selectedCategory: string = 'All Stories';

  currentPage: number = 1;
  postsPerPage: number = 6;

  categories = [
    { label: 'All Stories', icon: '▦' },
    { label: 'Cafe Updates', icon: '☕' },
    { label: 'Coffee Culture', icon: '◉' },
    { label: 'Brewing Guide', icon: '⌁' },
    { label: 'Events', icon: '▣' }
  ];

  blogPosts: BlogPost[] = [
    {
      id: 1,
      title: 'The Art of a Perfect Cup',
      slug: 'the-art-of-a-perfect-cup',
      category: 'Coffee Culture',
      categoryLabel: 'COFFEE CULTURE',
      excerpt:
        'Great coffee is more than a drink — it is a moment, a craft, and a passion. Discover how we bring the best beans, roasted to perfection, straight to your cup.',
      date: 'May 20, 2025',
      readTime: '5 min read',
      image: '/assets/images/art-of-cup.jpg',
      featured: true
    },
    {
      id: 2,
      title: 'Cold Brew vs Iced Coffee: What’s the Difference?',
      slug: 'cold-brew-vs-iced-coffee',
      category: 'Brewing Guide',
      categoryLabel: 'BREWING GUIDE',
      excerpt:
        'Learn the key differences between cold brew and iced coffee and discover which chilled coffee experience suits your taste best.',
      date: 'May 18, 2025',
      readTime: '4 min read',
      image: '/assets/images/cold-brew-vs-iced-coffee.jpg'
    },
    {
      id: 3,
      title: 'A Cozy Corner for Every Coffee Lover',
      slug: 'a-cozy-corner-for-every-coffee-lover',
      category: 'Cafe Updates',
      categoryLabel: 'CAFE UPDATES',
      excerpt:
        'Step inside Liberica MY Hub and experience a space designed for conversations, comfort, creativity, and great coffee.',
      date: 'May 15, 2025',
      readTime: '3 min read',
      image: '/assets/images/cozy-corner.jpg'
    },
    {
      id: 4,
      title: 'From Farm to Cup: Our Coffee Journey',
      slug: 'from-farm-to-cup',
      category: 'Coffee Culture',
      categoryLabel: 'COFFEE CULTURE',
      excerpt:
        'We take pride in ethical sourcing and the journey of our coffee beans from passionate farmers all the way to your cup.',
      date: 'May 10, 2025',
      readTime: '6 min read',
      image: '/assets/images/farm-to-cup.jpg'
    },
    {
      id: 6,
      title: 'New Desserts to Pair With Your Coffee',
      slug: 'new-desserts-to-pair-with-your-coffee',
      category: 'Cafe Updates',
      categoryLabel: 'CAFE UPDATES',
      excerpt:
        'Discover our latest dessert selections and find the perfect sweet companion for your favourite Liberica coffee.',
      date: 'May 5, 2025',
      readTime: '2 min read',
      image: '/assets/images/snacks-deserts.jpg'
    },
    // {
    //   id: 7,
    //   title: 'Upcoming Coffee Tasting Workshop',
    //   slug: 'upcoming-coffee-tasting-workshop',
    //   category: 'Events',
    //   categoryLabel: 'EVENTS',
    //   excerpt:
    //     'Join us for an immersive coffee tasting experience where you can explore aromas, flavours, origins and brewing techniques.',
    //   date: 'May 2, 2025',
    //   readTime: '2 min read',
    //   image: '/assets/images/liberica-visit-us-hero.jpg'
    // },
    {
      id: 8,
      title: 'Welcome to Liberica MY Hub',
      slug: 'welcome-to-liberica-my-hub',
      category: 'Cafe Updates',
      categoryLabel: 'CAFE UPDATES',
      excerpt:
        'Welcome to Liberica MY Hub — a place where artisan coffee, good food, meaningful conversations and Kuala Lumpur culture come together.',
      date: 'April 28, 2025',
      readTime: '4 min read',
      image: '/assets/images/welcome.jpg'
    },
    {
      id: 9,
      title: 'Why Liberica Coffee Is Special',
      slug: 'why-liberica-coffee-is-special',
      category: 'Coffee Culture',
      categoryLabel: 'COFFEE CULTURE',
      excerpt:
        'Explore the story behind Liberica coffee and discover what makes this unique coffee species so special.',
      date: 'April 24, 2025',
      readTime: '5 min read',
      image: '/assets/images/why-liberica-coffee.jpg'
    },
    {
      id: 11,
      title: 'Behind the Scenes at Liberica MY Hub',
      slug: 'behind-the-scenes-at-liberica-my-hub',
      category: 'Cafe Updates',
      categoryLabel: 'CAFE UPDATES',
      excerpt:
        'Take a look behind the scenes and meet the people who help make every Liberica MY Hub experience memorable.',
      date: 'April 16, 2025',
      readTime: '4 min read',
      image: '/assets/images/behind-liberica.jpg'
    }
  ];

  constructor() {}

  // Component Lifecycle
  ngOnInit(): void {
    this.checkReveal();
  }

  get filteredPosts(): BlogPost[] {
    let posts = this.blogPosts;

    if (this.selectedCategory !== 'All Stories') {
      posts = posts.filter(post => post.category === this.selectedCategory);
    }

    if (this.searchText && this.searchText.trim() !== '') {
      const search = this.searchText.toLowerCase().trim();

      posts = posts.filter(
        post =>
          post.title.toLowerCase().indexOf(search) !== -1 ||
          post.excerpt.toLowerCase().indexOf(search) !== -1 ||
          post.category.toLowerCase().indexOf(search) !== -1
      );
    }

    return posts;
  }

  get featuredPost(): BlogPost {
    return this.blogPosts.find(post => post.featured) || this.blogPosts[0];
  }

  get regularPosts(): BlogPost[] {
    return this.filteredPosts.filter(post => !post.featured);
  }

  get totalPages(): number {
    return Math.ceil(this.regularPosts.length / this.postsPerPage);
  }

  get paginatedPosts(): BlogPost[] {
    const startIndex = (this.currentPage - 1) * this.postsPerPage;
    const endIndex = startIndex + this.postsPerPage;

    return this.regularPosts.slice(startIndex, endIndex);
  }

  selectCategory(category: string): void {
    this.selectedCategory = category;
    this.currentPage = 1;
  }

  searchStories(): void {
    this.currentPage = 1;
  }

  clearSearch(): void {
    this.searchText = '';
    this.currentPage = 1;
  }

  nextPage(): void {
  if (this.currentPage < this.totalPages) {
      this.currentPage++;
  
      setTimeout(() => {
        this.scrollToTop();
      });
    }
  }
  
  previousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
  
      setTimeout(() => {
        this.scrollToTop();
      });
    }
  }
  
  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
  
      setTimeout(() => {
        this.scrollToTop();
      });
    }
  }

  trackByPost(index: number, post: BlogPost): number {
    return post.id;
  }

    // Scroll Animations
    @HostListener('window:scroll')
    onScroll(): void {
      this.checkReveal();
    }
  
    checkReveal(): void {
      const elements = document.querySelectorAll('.reveal');
      const windowHeight = window.innerHeight;
  
      elements.forEach((el: any) => {
        const position = el.getBoundingClientRect().top;
        if (position < windowHeight - 100) {
          el.classList.add('active');
        }
      });
    }
  
    // Smooth Scroll
    scrollTo(element: HTMLElement): void {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }

    private scrollToTop(): void {
    window.scrollTo({
    top: 0,
    behavior: 'smooth'
    });
  }
}